import * as React from 'react';
import {
    PVAPage,
    PVAActionsButtonGroup,
    LoadingIndicator,
    PVAStyle, PVATab, PVATabs, PVAContainer,
    DiamondRuler
} from '../../../components';
import { BaseRouteProps } from '../../../interfaces';
import { observer, inject } from 'mobx-react';
import {Style, StyleText, SurveyVersion, User, CharacteristicGroup, CharacteristicCompare, SurveyReportType, Characteristic} from '../../../models';
import { ReportDataService, StyleDataService, CharacteristicGroupDataService, SurveyReportTypeDataService } from '../../../services';
import { UserStore, FilterStore } from '../../../stores';
import { reaction, IReactionDisposer, toJS } from 'mobx';
import { exportToPDF } from '../../../utilities/Pdf';
import { PdfFoundations } from '../individual/pdf_foundations';
import { PdfUserGuide } from '../individual/pdf_user_guide';
import './FoundationsReport.css';
import '../../common/custom-content.css';


interface FoundationsReportProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
    useContentOnly?: boolean;
    selectedUserData?: any;
    styleData?: Style;
    hideDichotomies?: boolean;
}

interface NormalizedCompare {
    id: number;
    name: string;
    left: {
        characteristic: Characteristic;
        text: string;
    };
    right: {
        characteristic: Characteristic;
        text: string;
    };
    scoreType: string;
    scoreSide: string;
    threshold: number;
    score: number;
}

interface FoundationsReportState {
    pdfLoader: boolean;
    allStyles: Style[];
    characteristicGroups: { [id: number]: CharacteristicGroup };
    characteristics: { [id: number]: Characteristic };
    surveyReportTypes: SurveyReportType[];
}

const getContent = (currentStyle: Style, type: string) => {
    if (currentStyle && currentStyle.styleTexts) {
        const result = currentStyle.styleTexts.find((st: StyleText) => {
            return st.styleTextType === type;
        });
        if (result) {
            return result.value;
        }
    }
    return '';
};

export const FoundationsReportTopContent = (props: { style: Style }) => {
    const { style } = props;
    return (
        <div className="custom-content">
            <div className="custom-content-top" dangerouslySetInnerHTML={{ __html: getContent(style, 'top') }} />
        </div>
    );
};

export const FoundationsReportLeftAndRightPair = (props: { style: Style, badgeColor: string }) => {
    const { style, badgeColor } = props;
    return (
        <div className="custom-content">
            <div className="left-and-right-pair">
                <div className="custom-content-left icon-and-text" style={{ borderColor: badgeColor }}>
                    <img className="icon" src={style ? `/images/best-${style.name.toLowerCase()}.svg` : "/images/best.svg"}/>
                    <div className="text">
                        <span className="font-second-level title" style={{ color: badgeColor }}>At Their Best</span>
                        <div dangerouslySetInnerHTML={{ __html: getContent(style, 'left') }} />
                    </div>
                </div>
                <div className="custom-content-right icon-and-text">
                    <img className="icon" src="/images/worst.svg"/>
                    <div className="text">
                        <span className="font-second-level title">When Out of Balance</span>
                        <div dangerouslySetInnerHTML={{ __html: getContent(style, 'right') }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const FoundationsReportBottomContent = (props: { style: Style }) => {
    const { style } = props;
    return (
        <div className="custom-content">
            <div className="custom-content-bottom" dangerouslySetInnerHTML={{ __html: getContent(style, 'bottom') }} />
        </div>
    );
};

export const FoundationsReportTeamContent = (props: { style: Style }) => {
    const { style } = props;
    return (
        <div className="custom-content">
            <div className="custom-content-team" dangerouslySetInnerHTML={{ __html: getContent(style, 'team') }} />
        </div>
    );
};

export const FoundationsReportDichotomiesContent = (props: { normalizedCompares: NormalizedCompare[], badgeColor: string }) => {
    const { normalizedCompares, badgeColor } = props;
    if (!normalizedCompares || normalizedCompares.length === 0) {
        return null;
    }
    return (
        <div className="compare-blocks">
            {normalizedCompares.map((compare: NormalizedCompare) => {
                return <div key={compare.id} className="compare-block">
                    <div className={compare.scoreSide === 'left' ? 'left primary' : 'left'} style={compare.scoreSide === 'left' && badgeColor ? { borderColor: badgeColor, color: badgeColor } : undefined}>
                        <div className="characteristic-name">{compare.left.characteristic.name}</div>
                        <div className="custom-content" dangerouslySetInnerHTML={{ __html: compare.left.text}} />
                    </div>
                    <div className="chart">
                        <div className="name font-second-level">{compare.name}</div>
                        <div className="pronounced-labels">
                            <span className="font-heading">Pronounced</span>
                            <span className="font-heading">Pronounced</span>
                        </div>
                        <div className="score">
                            <DiamondRuler value={compare.score} activeColor={badgeColor} />
                        </div>
                    </div>
                    <div className={compare.scoreSide === 'right' ? 'right primary' : 'right'} style={compare.scoreSide === 'right' && badgeColor ? { borderColor: badgeColor, color: badgeColor } : undefined}>
                        <div className="characteristic-name">{compare.right.characteristic.name}</div>
                        <div className="custom-content" dangerouslySetInnerHTML={{ __html: compare.right.text}} />
                    </div>
                </div>
            })}
        </div>
    );
};

export const getNormalizedCompares = (selectedUserData: any, characteristicGroups: { [id: number]: CharacteristicGroup }, characteristics: { [id: number]: Characteristic }, surveyReportTypes: SurveyReportType[]) => {
    if (!selectedUserData || !selectedUserData.inclinationGroups || !characteristicGroups) {
        return [];
    }

    let dichotomiesReportIds = surveyReportTypes.filter(
        report => report.name.toLowerCase().includes('pva foundations')
    ).map(report => report.id);

    let comparesToShow = [];
    for (let group of selectedUserData.inclinationGroups) {
        if (dichotomiesReportIds.indexOf(group.surveyReportTypeID) < 0) continue;
        for (let compare of group.comparisons) {
            comparesToShow.push(compare);
        }
    }
    comparesToShow = comparesToShow.sort((a: any, b: any) => a.sort - b.sort);

    const mappedCompares = comparesToShow.map((compare: any) => {
        const leftText = compare.left;
        const rightText = compare.right;

        const characteristicLeft = characteristics[compare.characteristicLeftID] || compare.characteristicLeft || (compare.characteristicLeftName ? { id: compare.characteristicLeftID, name: compare.characteristicLeftName } : undefined);
        const characteristicRight = characteristics[compare.characteristicRightID] || compare.characteristicRight || (compare.characteristicRightName ? { id: compare.characteristicRightID, name: compare.characteristicRightName } : undefined);

        if (!characteristicLeft || !characteristicRight) {
            return null;
        }

        const getScore = (score: any) => {
            if (score && typeof score === 'object' && score.hasOwnProperty('parsedValue')) {
                return score.parsedValue;
            }
            return score || 0;
        };

        const leftScore = getScore(compare.characteristicLeftScore);
        const rightScore = getScore(compare.characteristicRightScore);

        let score = 0;
        if (compare.characteristicCompareScoreType === 'forceGreater') {
            if (compare.characteristicCompareScoreSide === 'left') {
                score = leftScore > compare.threshold ? -leftScore : rightScore;
            } else {
                score = rightScore > compare.threshold ? rightScore : -leftScore;
            }
        } else if (compare.characteristicCompareScoreType === 'forceLessThan') {
            if (compare.characteristicCompareScoreSide === 'left') {
                score = leftScore < compare.threshold ? -leftScore : rightScore;
            } else {
                score = rightScore < compare.threshold ? rightScore : -leftScore;
            }
        } else {
            score = leftScore > rightScore ? -leftScore : rightScore;
        }

        return {
            id: compare.id,
            name: compare.name,
            left: {
                characteristic: characteristicLeft || compare.characteristicLeft,
                text: leftText ? leftText : ''
            },
            right: {
                characteristic: characteristicRight || compare.characteristicRight,
                text: rightText ? rightText : ''
            },
            scoreType: compare.characteristicCompareScoreType,
            scoreSide: score < 0 ? 'left' : 'right',
            threshold: compare.threshold,
            score: score
        };
    });
    return mappedCompares.filter((compare: NormalizedCompare | null) => !!compare) as NormalizedCompare[];
};

@inject('UserStore', 'FilterStore') @observer
class FoundationsReportContent extends React.Component<FoundationsReportProps, FoundationsReportState> {
    private reportDataService = new ReportDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private surveyReportTypeDataService = new SurveyReportTypeDataService(this.props.notificationService);
    private disposer: IReactionDisposer;

    constructor(props: FoundationsReportProps) {
        super(props);

        this.state = {
            pdfLoader: false,
            allStyles: [],
            characteristicGroups: {},
            characteristics: {},
            surveyReportTypes: []
        };
    }
    
    private get hideDichotomies() {
        return this.props.hideDichotomies === undefined ? false : this.props.hideDichotomies;
    }

    componentDidMount() {
        this.disposer = reaction(
            () => ({
                surveyVersion: this.props.FilterStore!.surveyVersion,
                selectedUserData: this.selectedUserData,
                hideDichotomies: this.hideDichotomies
            }),
            ({ surveyVersion, selectedUserData, hideDichotomies }) => {
                if (surveyVersion) {
                    this.loadStyles(surveyVersion);
                    this.loadSurveyReportTypes(surveyVersion);
                }
                if (selectedUserData && !hideDichotomies) {
                    this.loadCharacteristicGroups(selectedUserData);
                }
            },
            { fireImmediately: true }
        );
    }

    componentWillUnmount() {
        if (this.disposer) {
            this.disposer();
        }
    }

    private loadStyles(surveyVersion: any) {
        this.styleDataService.getAllBySurveyVersionId(surveyVersion!.id).then((styles: Style[]) => {
            this.setState({ allStyles: styles });
        });
    }

    private loadSurveyReportTypes(surveyVersion: any) {
        this.surveyReportTypeDataService.getBySurveyVersion(surveyVersion.id).then((surveyReportTypes: SurveyReportType[]) => {
            this.setState({ surveyReportTypes: surveyReportTypes });
        });
    }

    private loadCharacteristicGroups(selectedUserData: any) {
        if (this.hideDichotomies) {
            return;
        }
        if (selectedUserData && selectedUserData.inclinationGroups) {
            selectedUserData.inclinationGroups.forEach((inclinationGroup: any) => {
                const id = inclinationGroup.characteristicGroupID;
                if (id && !this.state.characteristicGroups[id]) {
                    this.characteristicGroupDataService.get(id).then((characteristicGroup: CharacteristicGroup) => {
                        this.setState((prevState) => {
                            const characteristics = { ...prevState.characteristics };
                            if (characteristicGroup.characteristicGroupCharacteristics && characteristicGroup.characteristicGroupCharacteristics.length > 0) {
                                characteristicGroup.characteristicGroupCharacteristics.forEach(c => {
                                    if (c.characteristic) {
                                        characteristics[c.characteristic.id] = c.characteristic;
                                    }
                                });
                            }
                            return {
                                characteristicGroups: {
                                    ...prevState.characteristicGroups,
                                    [id]: characteristicGroup
                                },
                                characteristics: characteristics
                            };
                        });
                    });
                }
            });
        }
    }

    private get selectedUserData() {
        if (this.props.selectedUserData) {
            return this.props.selectedUserData;
        }
        return this.props.FilterStore!.individualReportUserData;
    }

    private getCurrentStyle() {
        if (this.props.styleData) {
            return this.props.styleData;
        }
        if (this.selectedUserData && this.selectedUserData.style) {
            let userStyle = this.selectedUserData.style;
            if (this.state.allStyles && this.state.allStyles.length > 0) {
                const foundStyle = this.state.allStyles.find((style: Style) => {
                    return style.name === this.selectedUserData.style.name;
                });
                if (foundStyle) {
                    userStyle = foundStyle;
                }
            }
            return userStyle;
        }
        return null;
    }

    private content(currentStyle: Style, type: string) {
        if (currentStyle && currentStyle.styleTexts) {
            const result = currentStyle.styleTexts.find((st: StyleText) => {
                return st.styleTextType === type;
            });
            if (result) {
                return result.value;
            }
        }
        return '';
    }

    private exportFoundationsPDF() {
        const currentStyle = this.getCurrentStyle();
        if (!currentStyle || !this.selectedUserData) return;

        this.setState({ pdfLoader: true }, () => {
            const surveyVersion = (this.props.FilterStore && this.props.FilterStore.surveyVersion) || this.selectedUserData.surveyVersion;
            
            exportToPDF(
                <PdfFoundations 
                    {...this.props}
                    selectedUserData={this.selectedUserData}
                    allStyles={this.state.allStyles}
                    surveyVersion={surveyVersion}
                    characteristicGroups={this.state.characteristicGroups}
                    characteristics={this.state.characteristics}
                    surveyReportTypes={this.state.surveyReportTypes}
                    notificationService={this.props.notificationService}
                />,
                `PVA_Foundations_${this.selectedUserData.name}.pdf`
            );
            this.setState({ pdfLoader: false });
        });
    }

    private exportNewUserGuidePDF() {
        this.setState({ pdfLoader: true }, () => {
            const user = this.props.UserStore && this.props.UserStore.userInfo;
            const userName = user ? `${user.firstName} ${user.lastName}` : '';
            const organization = (user && user.organization) ? user.organization.name : '';

            exportToPDF(
                <PdfUserGuide 
                    userName={userName}
                    organization={organization}
                />,
                `PVA_Style_Report_User_Guide.pdf`
            );
            this.setState({ pdfLoader: false });
        });
    }

    private get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    private get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }

    render() {
        const hideDichotomies = this.hideDichotomies;
        const actionGroups: PVAActionsButtonGroup[] = [];
        const exportItems = [];
        let normalizedCompares: NormalizedCompare[] = [];
        
        if (this.selectedUserData && this.selectedUserData.inclinationGroups && this.state.characteristicGroups) {
            normalizedCompares = getNormalizedCompares(this.selectedUserData, this.state.characteristicGroups, this.state.characteristics, this.state.surveyReportTypes);
        }

        exportItems.push({
            id: 'my-pva-style-report',
            name: 'My PVA Style Report',
            icon: 'Icon awesome-file-pdf.svg',
            description: 'My PVA Style Report in PDF Format',
            callback: () => this.exportFoundationsPDF()
        });

        exportItems.push({
            id: 'pva-style-report-user-guide',
            name: "PVA Style Report User's Guide",
            icon: 'Icon awesome-file-pdf.svg',
            description: "PVA Style Report User's Guide in PDF Format",
            callback: () => this.exportNewUserGuidePDF()
        });

        actionGroups.push({ header: 'Export Options', items: exportItems });

        const currentStyle = this.getCurrentStyle();
        const badgeColor = currentStyle ? `var(--pva-${currentStyle.name.toLowerCase()})` : 'var(--blue)';

        const styleTexts = currentStyle && currentStyle.styleTexts || [];
        const sortedAdditionalStyles = styleTexts.filter((styleText: StyleText) => {
            return styleText.styleTextType === 'additional';
        }).sort((one: StyleText, two: StyleText) => {
            if (one.sort > two.sort) {
                return 1;
            } else if (one.sort < two.sort) {
                return -1;
            }
            return 0;
        });

        const currentStyleName = currentStyle ? currentStyle.name.toLowerCase() : '';
        const checkmarkUrl = currentStyleName ? `url("/images/checkmark-${currentStyleName}.svg")` : 'url("/images/checkmark.svg")';
        const checkMarkRedUrl = currentStyleName ? `url("/images/check-mark-${currentStyleName}.svg")` : 'url("/images/check mark red.svg")';

        const body = (
            <PVAPage.Body>
                {this.state.pdfLoader && <LoadingIndicator showInPanel={true} showPdfDescription={true} />}
                {(!this.selectedUserData && !this.props.styleData || this.state.allStyles.length < 1) && !hideDichotomies && <LoadingIndicator />}
                {(this.selectedUserData || this.props.styleData || hideDichotomies) && currentStyle && (this.state.allStyles.length > 0) && 
                    <PVATabs activeColor={badgeColor}>
                        <PVATab>
                            <PVATab.Title>
                                Summary
                            </PVATab.Title>
                            <PVATab.Content>
                                <PVAStyle
                                    style={currentStyle}
                                    name={this.selectedUserData ? (this.selectedUserData.name || ' ') : ' '}
                                    brief={this.content(currentStyle, 'brief')}
                                    content={this.content(currentStyle, 'content')}
                                    notificationService={this.props.notificationService}
                                    history={this.props.history}
                                    location={this.props.location}
                                    match={this.props.match}
                                    showSecondaryStyle={false}
                                    showTraits={true}
                                    onViewAll={() => this.props.history.push('/pvastyles')}
                                    surveyVersion={this.surveyVersion}
                                    allStyles={this.state.allStyles}
                                    selectedUserData={this.selectedUserData}
                                    hideDichotomies={hideDichotomies}
                                />
                                <PVAContainer id={"Style Summary"} title={"Style Summary"}>
                                    <div style={{ '--custom-content-color': badgeColor, '--checkmark-url': checkmarkUrl, '--check-mark-url': checkMarkRedUrl } as any}>
                                        <FoundationsReportTopContent style={currentStyle} />
                                        <FoundationsReportLeftAndRightPair style={currentStyle} badgeColor={badgeColor} />
                                        <FoundationsReportBottomContent style={currentStyle} />
                                        {/*<div className="custom-content-additional" dangerouslySetInnerHTML={{ __html: this.content(currentStyle, 'additional') }} />*/}
                                        <FoundationsReportTeamContent style={currentStyle} />
                                    </div>
                                </PVAContainer>
                                {!hideDichotomies && normalizedCompares.length > 0 && <PVAContainer id={"compares"} title={"Key Dichotomies"}>
                                    <FoundationsReportDichotomiesContent normalizedCompares={normalizedCompares} badgeColor={badgeColor} />
                                </PVAContainer>}
                            </PVATab.Content>
                        </PVATab>
                        {sortedAdditionalStyles.map((styleText: StyleText) => {
                            return <PVATab key={styleText.keyName}>
                                <PVATab.Title>
                                    {styleText.keyName}
                                </PVATab.Title>
                                <PVATab.Content>
                                    {
                                        styleText.keyName && <div className={`custom-content ${styleText.keyName.toLowerCase().trim().replace(" ", "-")}-content`} style={{ '--custom-content-color': badgeColor, '--checkmark-url': checkmarkUrl, '--check-mark-url': checkMarkRedUrl } as any} key={styleText.value} dangerouslySetInnerHTML={{ __html: styleText.value}}/>
                                    }
                                </PVATab.Content>
                            </PVATab>;
                        })}
                    </PVATabs>
                }
            </PVAPage.Body>
        );

        if (this.props.useContentOnly) {
            return body;
        }

        return (
            <PVAPage>
                <PVAPage.Header actions={actionGroups}>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            <span className="greyDark">PVA Style for: </span>
                            <span>{this.selectedUserData ? this.selectedUserData.name : ''}</span>
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                </PVAPage.Header>
                {body}
                {this.selectedUserData && <PVAPage.Footer />}
            </PVAPage>
        );
    }
}

@inject('UserStore', 'FilterStore') @observer
export class FoundationsReport extends React.Component<FoundationsReportProps, FoundationsReportState> {
    render() {
        return <FoundationsReportContent {...this.props} />;
    }
}

export { FoundationsReportContent };



// WEBPACK FOOTER //
// ./src/components/reports/myreport/FoundationsReport.tsx